import { useState } from 'react';
import { Link } from 'react-router-dom';
import { qs } from '../lib/api.js';
import { useApi, useDebounced } from '../lib/hooks.js';
import { Loading, ErrorNote, Empty, FlashNote, Pill, relativeDate } from '../components/ui.jsx';

/** Req #1: browse the master items list — everything ever purchased. */
export default function Items() {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [staplesOnly, setStaplesOnly] = useState(false);
  const debounced = useDebounced(query, 250);

  const items = useApi(`/items${qs({ q: debounced, category, staple: staplesOnly || undefined, limit: 300 })}`);
  const categories = useApi('/items/categories');

  return (
    <div className="stack">
      <FlashNote />
      <input
        type="search"
        placeholder="Search — receipt shorthand works too"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        aria-label="Search master items"
      />

      <div className="chips scroll-x">
        <button type="button" className={`chip ${!category && !staplesOnly ? 'active' : ''}`}
          onClick={() => { setCategory(''); setStaplesOnly(false); }}>
          All
        </button>
        <button type="button" className={`chip ${staplesOnly ? 'active' : ''}`}
          onClick={() => { setStaplesOnly((v) => !v); setCategory(''); }}>
          Staples
        </button>
        {(categories.data || []).map((entry) => (
          <button
            key={entry.category}
            type="button"
            className={`chip ${category === entry.category ? 'active' : ''}`}
            onClick={() => { setCategory(category === entry.category ? '' : entry.category); setStaplesOnly(false); }}
            style={{ textTransform: 'capitalize' }}
          >
            {entry.category} <span className="tiny muted">{entry.n}</span>
          </button>
        ))}
      </div>

      {items.loading && !items.data && <Loading />}
      <ErrorNote error={items.error} onRetry={items.reload} />

      {items.data && (
        <div className="small muted">
          {items.data.total} item{items.data.total === 1 ? '' : 's'} ever purchased
        </div>
      )}

      {items.data?.items?.length === 0 && (
        <Empty title="Nothing here">
          The master list fills itself as you scan receipts and import purchase history.
        </Empty>
      )}

      <div className="card list">
        {(items.data?.items || []).map((item) => (
          <Link
            key={item.id}
            className="list-item"
            to={`/items/${item.id}`}
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            <div className="grow" style={{ minWidth: 0 }}>
              <div className="truncate">
                {item.name}
                {item.brand && <span className="muted small"> · {item.brand}</span>}
              </div>
              <div className="tiny muted truncate">
                {item.category}
                {item.times_purchased > 0 && ` · bought ${item.times_purchased}×`}
                {item.last_purchased_at && ` · last ${relativeDate(item.last_purchased_at)}`}
              </div>
            </div>
            {item.is_staple && <Pill>staple</Pill>}
            {item.on_hand > 0
              ? <Pill tone="ok">{Math.round(item.on_hand * 100) / 100}</Pill>
              : <Pill>out</Pill>}
          </Link>
        ))}
      </div>
    </div>
  );
}
